"use client";

import {
  type CSSProperties,
  useEffect,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import PrototypePlanet, {
  type FaqCategory,
  type PlanetMotionMode,
  type PlanetMotionState,
} from "@/components/PrototypePlanet";
import {
  faqCategories,
  faqs,
  type FaqUiCategory,
} from "@/lib/faq-content";

const getAssetUrl = (path: string): string => {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) return path;
  const base = import.meta.env.BASE_URL || "/";
  const prefix = base.endsWith("/") ? base : `${base}/`;
  return `${prefix}${path.startsWith("/") ? path.slice(1) : path}`;
};

const menuItems = [
  { number: "01", label: "Hero", href: "/#hero" },
  { number: "02", label: "Why FTO / How We Deliver", href: "/#signal" },
  { number: "03", label: "Live Workflow", href: "/#workflow" },
  { number: "04", label: "Services & Workflows", href: "/#solutions" },
  { number: "05", label: "Industries", href: "/#industries" },
  { number: "06", label: "Global Delivery", href: "/#standard" },
  { number: "—", label: "FAQ", href: "/faq", current: true },
  { number: "07", label: "Start the Conversation", href: "/#contact" },
];

const initialPlanetMotion: PlanetMotionState = {
  preference: "auto",
  mode: "full",
  speed: 1,
  precision: 0,
  aiCapabilityFocus: null,
  brandSignal: 0,
  brandSignalFocus: null,
  judgment: 0,
  judgmentFocus: null,
  outcomes: 0,
  outcomeFocus: null,
  outcomeSequence: 0,
  industries: 0,
  industryFocus: null,
  industryPreview: false,
  industrySequence: 0,
  questions: 1,
  questionCategory: "about",
  questionPreview: false,
  questionSequence: 0,
  perspectives: 0,
  perspectiveFocus: null,
  aiWorld: 0,
  humanWorld: 0,
  contact: 0,
};

const getPlanetCategory = (category: FaqUiCategory): FaqCategory =>
  category === "commercial" ? "ai-human" : category;

export default function FaqPageClient() {
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const faqTabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const questionSequenceRef = useRef(0);
  const planetMotionRef = useRef<PlanetMotionState>({ ...initialPlanetMotion });
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(0);
  const [previewFaq, setPreviewFaq] = useState<number | null>(null);
  const [motionMode, setMotionMode] = useState<PlanetMotionMode>("full");

  const activeFaqIndex = previewFaq ?? selectedFaq;
  const activeFaq = faqs[activeFaqIndex];
  const activeFaqCategory = faqCategories.find(({ key }) => key === activeFaq.category)
    ?? faqCategories[0];
  const activeFaqCategoryIndexes = faqs.reduce<number[]>((indexes, faq, index) => {
    if (faq.category === activeFaqCategory.key) indexes.push(index);
    return indexes;
  }, []);

  const applyFaqCategory = (category: FaqUiCategory, preview: boolean) => {
    planetMotionRef.current = {
      ...planetMotionRef.current,
      questions: 1,
      questionCategory: getPlanetCategory(category),
      questionPreview: preview,
      questionSequence: questionSequenceRef.current,
    };
  };

  const previewFaqAnswer = (index: number | null) => {
    setPreviewFaq(index);
    applyFaqCategory(faqs[index ?? selectedFaq].category, index !== null);
  };

  const selectFaqAnswer = (index: number) => {
    setSelectedFaq(index);
    setPreviewFaq(null);
    questionSequenceRef.current += 1;
    applyFaqCategory(faqs[index].category, false);
  };

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncMotionMode = () => {
      const nextMode: PlanetMotionMode = preference.matches ? "reduced" : "full";
      setMotionMode(nextMode);
      planetMotionRef.current = {
        ...planetMotionRef.current,
        preference: preference.matches ? "reduced" : "auto",
        mode: nextMode,
      };
    };
    syncMotionMode();
    preference.addEventListener("change", syncMotionMode);
    return () => preference.removeEventListener("change", syncMotionMode);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const menuFocusable = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(
      "button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])",
    ) ?? []);
    const focusable = menuButtonRef.current
      ? [...menuFocusable, menuButtonRef.current]
      : menuFocusable;
    focusable[0]?.focus();

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (event.key !== "Tab" || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [menuOpen]);

  const renderRelatedLinks = (
    related: Array<{ label: string; chapter: string }>,
    context: "desktop" | "mobile",
  ) => (
    <nav className="faq-related-links" aria-label={`Related links for this answer on ${context}`}>
      {related.map(({ label, chapter }) => (
        <Link href={`/#${chapter}`} key={`${context}-${label}`}>
          <span>{label}</span><i aria-hidden="true">→</i>
        </Link>
      ))}
    </nav>
  );

  return (
    <main className="prototype-shell faq-page-shell">
      <a className="skip-link" href="#faq-content">Skip to FAQ</a>
      <section
        className="stage faq-page-stage"
        data-menu-open={menuOpen ? "true" : "false"}
        data-faq-ready="true"
        data-faq-category={getPlanetCategory(activeFaq.category)}
        data-faq-preview={previewFaq !== null ? "true" : "false"}
        data-motion-mode={motionMode}
        aria-label="FTO frequently asked questions"
      >
        <header className="prototype-topbar">
          <Link className="brand-mark" href="/" aria-label="FTO — Home">
            {/* eslint-disable-next-line @next/next/no-img-element -- Preserve the supplied official SVG without transformation. */}
            <img src={getAssetUrl("/fto-logo.svg")} alt="" width="500" height="500" aria-hidden="true" />
          </Link>
          <div className="prototype-label">
            <span>Experience architecture</span>
            <strong>FAQ — Frequently Asked Questions</strong>
          </div>
          <div className="topbar-actions">
            <Link className="global-contact-cta" href="/#contact">
              LET’S TALK <i aria-hidden="true">→</i>
            </Link>
            <button
              className="menu-trigger"
              ref={menuButtonRef}
              type="button"
              aria-expanded={menuOpen}
              aria-controls="experience-menu"
              aria-label={menuOpen ? "Close experience menu" : "Open experience menu"}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span>{menuOpen ? "Close" : "Menu"}</span>
              <i aria-hidden="true"><b /><b /></i>
            </button>
          </div>
        </header>

        <div
          className="experience-menu"
          id="experience-menu"
          ref={menuRef}
          role="dialog"
          aria-modal="true"
          aria-label="Experience navigation"
          aria-hidden={!menuOpen}
          inert={!menuOpen}
        >
          <div className="menu-atmosphere" aria-hidden="true"><span>FTO</span><i /></div>
          <div className="menu-intro">
            <p>FTO EXPERIENCE SYSTEM</p>
            <h2>Follow the signals.</h2>
            <span>Move directly to any chapter in the experience.</span>
          </div>
          <nav className="menu-chapters" aria-label="FTO experience chapters">
            {menuItems.map((item) => (
              <Link
                className={item.current ? "active" : ""}
                href={item.href}
                aria-current={item.current ? "page" : undefined}
                onClick={() => setMenuOpen(false)}
                key={item.href}
              >
                <span>{item.number}</span>
                <strong>{item.label}</strong>
                <i>↗</i>
              </Link>
            ))}
          </nav>
          <Link className="menu-cta" href="/#contact">
            LET’S TALK <i>→</i>
          </Link>
        </div>

        <div className="planet-wrap" aria-hidden="true">
          <div className="planet-aura" />
          <PrototypePlanet motionState={planetMotionRef} />
        </div>

        <article
          className="chapter-panel"
          id="faq"
          data-side="right"
          data-density="dense"
          data-active="true"
          aria-label="FAQ"
          style={{
            "--chapter-accent": "#d2e3f3",
            "--chapter-rgb": "210,227,243",
            "--clarity": 1,
            "--travel": 0,
            "--seed": 1,
          } as CSSProperties}
        >
          <div className="chapter-grid" aria-hidden="true" />
          <header className="chapter-header">
            <span>FAQ</span>
            <i />
            <strong>Frequently Asked Questions</strong>
          </header>
          <div className="chapter-content" id="faq-content" tabIndex={-1}>
            <p className="chapter-kicker">FREQUENTLY ASKED QUESTIONS</p>
            <h1>Clarity before complexity.</h1>
            <p className="chapter-body">
              Clear answers about FTO, global outsourcing, delivery models, industries, and the role of AI and human judgment.
            </p>

            <section
              className="clarity-engine"
              aria-label="The Clarity Engine"
              style={{
                "--faq-accent": activeFaqCategory.accent,
                "--faq-rgb": activeFaqCategory.rgb,
              } as CSSProperties}
            >
              <h2 className="clarity-engine-label">
                <span>The Clarity Engine</span><i aria-hidden="true" />
              </h2>

              <div
                className="clarity-engine-desktop"
                onBlur={(event) => {
                  const nextTarget = event.relatedTarget;
                  if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
                    previewFaqAnswer(null);
                  }
                }}
              >
                <div className="faq-category-tabs" role="group" aria-label="FAQ categories">
                  {faqCategories.map((category) => {
                    const isCurrent = activeFaqCategory.key === category.key;
                    return (
                      <button
                        type="button"
                        aria-pressed={isCurrent}
                        data-current={isCurrent ? "true" : "false"}
                        style={{
                          "--category-accent": category.accent,
                          "--category-rgb": category.rgb,
                        } as CSSProperties}
                        onClick={() => {
                          const firstQuestion = faqs.findIndex((faq) => faq.category === category.key);
                          if (firstQuestion >= 0) selectFaqAnswer(firstQuestion);
                        }}
                        key={category.key}
                      >
                        <i aria-hidden="true" />
                        <span>{category.label}</span>
                      </button>
                    );
                  })}
                </div>

                <div
                  className="faq-index"
                  role="group"
                  aria-label={`${activeFaqCategory.label} frequently asked questions`}
                >
                  <section
                    className="faq-category"
                    data-category={activeFaqCategory.key}
                    aria-labelledby={`faq-category-${activeFaqCategory.key}`}
                    style={{
                      "--category-accent": activeFaqCategory.accent,
                      "--category-rgb": activeFaqCategory.rgb,
                    } as CSSProperties}
                  >
                    <h3 id={`faq-category-${activeFaqCategory.key}`}>
                      <i aria-hidden="true" />
                      <span>{activeFaqCategory.label}</span>
                      <small>{activeFaqCategoryIndexes.length} questions</small>
                    </h3>
                    <div>
                      {faqs.map((faq, index) => {
                        if (faq.category !== activeFaqCategory.key) return null;
                        const isSelected = selectedFaq === index;
                        const isCurrent = activeFaqIndex === index;
                        return (
                          <button
                            type="button"
                            id={`faq-tab-${index + 1}`}
                            aria-controls={`faq-answer-${index + 1}`}
                            aria-expanded={isCurrent}
                            aria-pressed={isSelected}
                            tabIndex={isSelected ? 0 : -1}
                            data-current={isCurrent ? "true" : "false"}
                            data-selected={isSelected ? "true" : "false"}
                            ref={(node) => { faqTabRefs.current[index] = node; }}
                            onPointerEnter={(event) => {
                              if (event.pointerType === "mouse") previewFaqAnswer(index);
                            }}
                            onPointerLeave={(event) => {
                              if (
                                event.pointerType === "mouse"
                                && document.activeElement !== event.currentTarget
                              ) {
                                previewFaqAnswer(null);
                              }
                            }}
                            onFocus={() => previewFaqAnswer(index)}
                            onClick={() => selectFaqAnswer(index)}
                            onKeyDown={(event) => {
                              const position = activeFaqCategoryIndexes.indexOf(index);
                              let next = index;
                              if (event.key === "ArrowDown" || event.key === "ArrowRight") {
                                next = activeFaqCategoryIndexes[(position + 1) % activeFaqCategoryIndexes.length];
                              } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
                                next = activeFaqCategoryIndexes[
                                  (position - 1 + activeFaqCategoryIndexes.length) % activeFaqCategoryIndexes.length
                                ];
                              } else if (event.key === "Home") {
                                next = activeFaqCategoryIndexes[0];
                              } else if (event.key === "End") {
                                next = activeFaqCategoryIndexes[activeFaqCategoryIndexes.length - 1];
                              } else {
                                return;
                              }
                              event.preventDefault();
                              faqTabRefs.current[next]?.focus();
                            }}
                            key={faq.question}
                          >
                            <span className="faq-question-number">{String(index + 1).padStart(2, "0")}</span>
                            <span className="faq-question-copy">{faq.question}</span>
                            <i className="faq-question-state" aria-hidden="true"><b /><b /></i>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                </div>

                <div className="answer-field" data-category={activeFaq.category}>
                  <span className="answer-field-label">Answer Field</span>
                  <p className="faq-live-status" aria-live="polite" aria-atomic="true">
                    {`Answer ${String(activeFaqIndex + 1).padStart(2, "0")}: ${activeFaq.question}`}
                  </p>
                  {faqs.map((faq, index) => {
                    const category = faqCategories.find(({ key }) => key === faq.category)
                      ?? faqCategories[0];
                    const isCurrent = activeFaqIndex === index;
                    return (
                      <article
                        className={`faq-answer-panel${isCurrent ? " is-current" : ""}`}
                        id={`faq-answer-${index + 1}`}
                        role="region"
                        aria-labelledby={`faq-tab-${index + 1}`}
                        aria-hidden={!isCurrent}
                        inert={!isCurrent}
                        style={{
                          "--answer-accent": category.accent,
                          "--answer-rgb": category.rgb,
                        } as CSSProperties}
                        key={faq.question}
                      >
                        <span className="answer-watermark" aria-hidden="true">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <header>
                          <span>Answer {String(index + 1).padStart(2, "0")}</span>
                          <small>{category.label}</small>
                        </header>
                        <h3>{faq.question}</h3>
                        <i className="answer-accent-line" aria-hidden="true" />
                        <p>{faq.answer}</p>
                        <footer>
                          <span>Related</span>
                          {renderRelatedLinks(faq.related, "desktop")}
                        </footer>
                      </article>
                    );
                  })}
                </div>
              </div>

              <div className="clarity-engine-mobile">
                {faqCategories.map((category) => (
                  <section
                    className="faq-mobile-category"
                    data-category={category.key}
                    style={{
                      "--category-accent": category.accent,
                      "--category-rgb": category.rgb,
                    } as CSSProperties}
                    key={category.key}
                  >
                    <h3><i aria-hidden="true" /><span>{category.label}</span></h3>
                    {faqs.map((faq, index) => {
                      if (faq.category !== category.key) return null;
                      const expanded = selectedFaq === index;
                      return (
                        <div className="faq-mobile-item" data-expanded={expanded ? "true" : "false"} key={faq.question}>
                          <button
                            type="button"
                            id={`faq-accordion-${index + 1}`}
                            aria-controls={`faq-mobile-answer-${index + 1}`}
                            aria-expanded={expanded}
                            onClick={() => selectFaqAnswer(index)}
                          >
                            <span>{String(index + 1).padStart(2, "0")}</span>
                            <strong>{faq.question}</strong>
                            <i aria-hidden="true"><b /><b /></i>
                          </button>
                          <div
                            className="faq-mobile-answer"
                            id={`faq-mobile-answer-${index + 1}`}
                            role="region"
                            aria-labelledby={`faq-accordion-${index + 1}`}
                            aria-hidden={!expanded}
                            inert={!expanded}
                          >
                            <div>
                              <p>{faq.answer}</p>
                              {renderRelatedLinks(faq.related, "mobile")}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </section>
                ))}
              </div>

              <div className="faq-conversation-bridge">
                <span>Still deciding what the right model looks like?</span>
                <Link href="/#contact">
                  Define your delivery needs <i aria-hidden="true">→</i>
                </Link>
              </div>
            </section>
          </div>
          <footer className="chapter-footer">
            <span>FTO / EXPERIENCE SYSTEM</span>
            <strong>FAQ</strong>
          </footer>
        </article>
      </section>
    </main>
  );
}
